import React from "react";
import { Modal, Space, Tag, Typography } from "antd";
import { ExclamationCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import i18next from "i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { conflictText } from "./labels";

/** The warnings (need acknowledgement) and notes of a plan, one line each. */
export const ConflictList = ({ conflicts, t, compact = false }) => {
  if (!conflicts?.length) return null;
  return (
    <ul className={`dsp-conflicts${compact ? " dsp-conflicts--compact" : ""}`}>
      {conflicts.map((c, i) => (
        <li key={`${c.type}-${c.employee || c.vehicle || c.tool || ""}-${i}`}>
          {c.severity === "warning" ? (
            <ExclamationCircleOutlined className="dsp-conflicts__warn" />
          ) : (
            <InfoCircleOutlined className="dsp-conflicts__info" />
          )}
          <span>{conflictText(c, t)}</span>
        </li>
      ))}
    </ul>
  );
};

export const warningCount = conflicts =>
  (conflicts || []).filter(c => c.severity === "warning").length;

export const ConflictBadge = ({ conflicts, t }) => {
  const warnings = warningCount(conflicts);
  if (!warnings) return null;
  return (
    <Tag color="warning" className="dsp-badge" title={t("Warnings")}>
      <ExclamationCircleOutlined /> {warnings}
    </Tag>
  );
};

/**
 * Runs `send(acknowledge)`; when the server answers 409 with conflicts, lists them and offers to
 * save anyway (the server logs the acknowledgement). Resolves the response, or null when the
 * planner cancelled.
 */
export const saveWithAcknowledgement = async send => {
  const t = (key, options) => i18next.t(key, { ns: "dashboard", ...options });
  try {
    return await send(false);
  } catch (error) {
    const data = error?.response?.data;
    if (error?.response?.status !== 409 || data?.code !== "CONFLICTS") {
      generalErrorAlert(error);
      return null;
    }
    const confirmed = await new Promise(resolve =>
      Modal.confirm({
        rootClassName: "dispatch-page",
        title: t("Plan anyway?"),
        width: 560,
        icon: <ExclamationCircleOutlined />,
        content: (
          <Space direction="vertical" className="dsp-full">
            <ConflictList conflicts={data.conflicts} t={t} />
            <Typography.Text type="secondary">
              {t("Saving anyway is recorded in the job's history with these warnings.")}
            </Typography.Text>
          </Space>
        ),
        okText: t("Save anyway"),
        cancelText: t("Cancel"),
        onOk: () => resolve(true),
        onCancel: () => resolve(false)
      })
    );
    if (!confirmed) return null;
    try {
      return await send(true);
    } catch (second) {
      generalErrorAlert(second);
      return null;
    }
  }
};
